import { useState } from 'react';
import { PresentationTemplate } from '../../../mocks/presentationTemplates';

interface CompareTemplatesModalProps {
  templates: PresentationTemplate[];
  onClose: () => void;
  onSelectTemplate: (template: PresentationTemplate) => void;
}

// ── Local infographic type detection ─────────────────────────────────────
function detectCompareInfoType(t: PresentationTemplate): { label: string; icon: string; color: string; bg: string } {
  const text = (t.name + ' ' + t.tags.join(' ') + ' ' + t.description).toLowerCase();
  if (text.includes('timeline'))
    return { label: 'Timeline',     icon: 'ri-time-line',               color: '#0891b2', bg: '#e0f2fe' };
  if (text.includes('swot'))
    return { label: 'SWOT',         icon: 'ri-focus-3-line',             color: '#7c3aed', bg: '#ede9fe' };
  if (text.includes('dashboard'))
    return { label: 'Dashboard',    icon: 'ri-dashboard-2-line',         color: '#059669', bg: '#d1fae5' };
  if (text.includes('process') || text.includes('flow') || text.includes('cycle'))
    return { label: 'Process Flow', icon: 'ri-git-branch-line',          color: '#d97706', bg: '#fef3c7' };
  if (text.includes('pyramid'))
    return { label: 'Pyramid',      icon: 'ri-bar-chart-grouped-line',   color: '#e11d48', bg: '#ffe4e6' };
  if (text.includes('comparison') || text.includes('compare') || text.includes('vs'))
    return { label: 'Comparison',   icon: 'ri-scales-3-line',            color: '#db2777', bg: '#fce7f3' };
  return { label: 'Infographic',    icon: 'ri-pie-chart-2-line',         color: '#64748b', bg: '#f1f5f9' };
}

const FEATURES = [
  { key: 'infographicType', label: 'Infographic Type', icon: 'ri-pie-chart-2-line', type: 'infographicType' },
  { key: 'slideCount',      label: 'Slide Count',      icon: 'ri-layout-grid-line', type: 'number' },
  { key: 'category',        label: 'Category',         icon: 'ri-tag-line',         type: 'text' },
  { key: 'tags',            label: 'Style Tags',       icon: 'ri-price-tag-3-line', type: 'tags' },
  { key: 'badge',           label: 'Badge',            icon: 'ri-award-line',       type: 'badge' },
  { key: 'colorPalette',    label: 'Color Palette',    icon: 'ri-palette-line',     type: 'colors' },
  { key: 'exportFormats',   label: 'Export Formats',   icon: 'ri-download-line',    type: 'fixed', value: 'PDF, PPTX, Keynote, HTML, Video' },
  { key: 'animations',      label: 'Animations',       icon: 'ri-movie-line',       type: 'fixed', value: '12+ built-in' },
  { key: 'collaboration',   label: 'Collaboration',    icon: 'ri-team-line',        type: 'fixed', value: 'Real-time (up to 10)' },
  { key: 'aiFeatures',      label: 'AI Features',      icon: 'ri-sparkling-2-line', type: 'fixed', value: 'Outline, Notes, Rewrite' },
  { key: 'languages',       label: 'Languages',        icon: 'ri-translate-2',      type: 'fixed', value: '40+ supported' },
];

function getCellValue(template: PresentationTemplate, feature: typeof FEATURES[0]) {
  if (feature.type === 'fixed') {
    return <span className="text-xs text-slate-700">{feature.value}</span>;
  }
  if (feature.type === 'infographicType') {
    const info = detectCompareInfoType(template);
    return (
      <div className="flex items-center gap-2">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: info.color }}
        >
          <i className={`${info.icon} text-white text-xs`}></i>
        </div>
        <span
          className="px-2.5 py-0.5 rounded-full text-xs font-bold text-white"
          style={{ backgroundColor: info.color }}
        >
          {info.label}
        </span>
      </div>
    );
  }
  if (feature.key === 'slideCount') {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-bold text-slate-900">{template.slideCount}</span>
        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-rose-400 to-fuchsia-400 rounded-full"
            style={{ width: `${Math.min((template.slideCount / 35) * 100, 100)}%` }}
          ></div>
        </div>
      </div>
    );
  }
  if (feature.key === 'category') {
    return <span className="px-2 py-0.5 bg-rose-50 text-rose-700 text-xs font-semibold rounded-full">{template.category}</span>;
  }
  if (feature.key === 'tags') {
    return (
      <div className="flex flex-wrap gap-1">
        {template.tags.map(tag => (
          <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">{tag}</span>
        ))}
      </div>
    );
  }
  if (feature.key === 'badge') {
    if (!template.badge) return <span className="text-xs text-slate-400">—</span>;
    const colors: Record<string, string> = {
      Popular: 'bg-amber-100 text-amber-700',
      New: 'bg-emerald-100 text-emerald-700',
      Trending: 'bg-rose-100 text-rose-700',
    };
    return <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full ${colors[template.badge!]}`}>{template.badge}</span>;
  }
  if (feature.key === 'colorPalette') {
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex gap-1.5">
          {template.colorPalette.map((c, i) => (
            <div key={i} className="w-5 h-5 rounded-md border-2 border-white shadow-sm" title={c} style={{ backgroundColor: c }}></div>
          ))}
        </div>
        <div className="flex gap-0.5 h-1.5 rounded-full overflow-hidden w-full">
          {template.colorPalette.map((c, i) => (
            <div key={i} className="flex-1" style={{ backgroundColor: c }}></div>
          ))}
        </div>
      </div>
    );
  }
  return <span className="text-xs text-slate-400">—</span>;
}

function ScoreBar({ value, max }: { value: number; max: number }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-slate-500">Suitability</span>
        <span className="text-xs font-bold text-rose-600">{pct}%</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-rose-400 to-fuchsia-400 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
      </div>
    </div>
  );
}

export default function CompareTemplatesModal({ templates, onClose, onSelectTemplate }: CompareTemplatesModalProps) {
  const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null);

  const scores = templates.map(t => {
    let s = t.slideCount;
    if (t.badge === 'Popular') s += 12;
    if (t.badge === 'Trending') s += 8;
    if (t.badge === 'New') s += 5;
    return { id: t.id, score: s };
  });
  const maxScore = Math.max(...scores.map(s => s.score));

  const colWidth = templates.length === 2 ? 'w-1/2' : templates.length === 3 ? 'w-1/3' : 'w-1/4';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-rose-500 to-fuchsia-500 rounded-xl flex items-center justify-center">
              <i className="ri-scales-3-line text-white text-base"></i>
            </div>
            <div>
              <h2 className="font-extrabold text-slate-900 text-base">Compare Templates</h2>
              <p className="text-xs text-slate-400">Side-by-side comparison of {templates.length} templates</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-slate-100 text-slate-500 cursor-pointer transition-colors">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* ── Table ── */}
        <div className="overflow-y-auto flex-1" style={{ scrollbarWidth: 'thin' }}>
          <table className="w-full table-fixed">
            {/* Template header row */}
            <thead className="sticky top-0 z-10">
              <tr className="bg-white border-b border-slate-100 shadow-sm">
                <th className="w-36 px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Feature</th>
                {templates.map(template => {
                  const info = detectCompareInfoType(template);
                  return (
                    <th key={template.id} className={`${colWidth} px-4 py-3`}>
                      <div
                        className={`rounded-xl overflow-hidden border-2 transition-all ${
                          hoveredTemplate === template.id ? 'border-rose-400' : 'border-slate-200'
                        }`}
                        onMouseEnter={() => setHoveredTemplate(template.id)}
                        onMouseLeave={() => setHoveredTemplate(null)}
                      >
                        {/* Thumbnail */}
                        <div className="aspect-video relative overflow-hidden bg-slate-100">
                          <img src={template.thumbnail} alt={template.name} className="w-full h-full object-cover" />
                          {/* ── Infographic type badge overlaid on thumbnail ── */}
                          <div
                            className="absolute bottom-0 left-0 right-0 flex items-center gap-2 px-2.5 py-2"
                            style={{ background: `linear-gradient(to top, ${info.color}ee, transparent)` }}
                          >
                            <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 bg-white/20">
                              <i className={`${info.icon} text-white text-xs`}></i>
                            </div>
                            <span className="text-xs font-bold text-white drop-shadow">{info.label}</span>
                          </div>
                          {/* Status badge */}
                          {template.badge && (() => {
                            const bc: Record<string, string> = {
                              Popular: 'bg-amber-400 text-white',
                              New: 'bg-emerald-500 text-white',
                              Trending: 'bg-rose-500 text-white',
                            };
                            return (
                              <div className={`absolute top-2 right-2 px-2 py-0.5 text-xs font-bold rounded-full ${bc[template.badge!]}`}>
                                {template.badge}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Card info */}
                        <div className="p-3 bg-white">
                          {/* ── Infographic type pill always visible ── */}
                          <div className="flex items-center gap-1.5 mb-2">
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold text-white"
                              style={{ backgroundColor: info.color }}
                            >
                              <i className={`${info.icon} text-[10px]`}></i>
                              {info.label}
                            </span>
                          </div>
                          <p className="text-sm font-bold text-slate-900 leading-tight line-clamp-1">{template.name}</p>
                          <p className="text-xs text-slate-500 line-clamp-2 mt-1 leading-relaxed">{template.description}</p>
                          {/* Palette strip */}
                          <div className="flex gap-0.5 h-1.5 rounded-full overflow-hidden mt-2.5">
                            {template.colorPalette.map((c, i) => (
                              <div key={i} className="flex-1" style={{ backgroundColor: c }}></div>
                            ))}
                          </div>
                          <ScoreBar value={scores.find(s => s.id === template.id)?.score ?? 0} max={maxScore + 5} />
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {FEATURES.map((feature, fi) => (
                <tr key={feature.key} className={`border-b border-slate-50 ${fi % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-rose-50/30 transition-colors`}>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-rose-50 rounded-md flex items-center justify-center flex-shrink-0">
                        <i className={`${feature.icon} text-rose-400 text-xs`}></i>
                      </div>
                      <span className="text-xs font-semibold text-slate-600 whitespace-nowrap">{feature.label}</span>
                    </div>
                  </td>
                  {templates.map(template => (
                    <td key={template.id} className={`${colWidth} px-4 py-3.5`}>
                      {getCellValue(template, feature)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── CTA footer ── */}
        <div className="flex-shrink-0 border-t border-slate-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex-1 hidden md:flex items-center gap-2">
              <i className="ri-information-line text-slate-400 text-sm"></i>
              <p className="text-xs text-slate-500">Suitability score is based on slide depth, popularity, and trending status.</p>
            </div>
            <div className="flex gap-3 ml-auto flex-wrap">
              {templates.map(template => {
                const info = detectCompareInfoType(template);
                return (
                  <button
                    key={template.id}
                    onClick={() => onSelectTemplate(template)}
                    className="flex items-center gap-2 px-4 py-2.5 text-white text-sm font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
                    style={{ background: `linear-gradient(135deg, ${info.color}, #f43f5e)` }}
                  >
                    <i className={`${info.icon} text-sm`}></i>
                    <span className="hidden lg:inline">Use </span>{template.name.split(' ').slice(0, 2).join(' ')}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
